import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import employeeService, { UpdateEmployeeDTO } from '../../services/employeeService';
import departmentService, { Department } from '../../services/departmentService';
import { userService, UserDTO } from '../../services/userService';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './HrFormPage.css';
import { ArrowLeft, Save } from 'lucide-react';

const MAX_LENGTHS = {
  employeeCode: 40,
  firstName: 100,
  lastName: 100,
  email: 150,
  phone: 30,
  jobTitle: 100,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Stored UTC instants render into a date input as plain calendar days. */
const toDateInput = (value?: string | null) => (value ? value.slice(0, 10) : '');

interface EmployeeForm {
  employeeCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  departmentId: number | '';
  userId: number | '';
  hireDate: string;
  terminationDate: string;
  isActive: boolean;
}

interface FieldErrors {
  employeeCode?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  jobTitle?: string;
  hireDate?: string;
  terminationDate?: string;
}

const EmployeeEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [formData, setFormData] = useState<EmployeeForm>({
    employeeCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    departmentId: '',
    userId: '',
    hireDate: '',
    terminationDate: '',
    isActive: true,
  });

  const loadLookups = useCallback(async () => {
    try {
      const [departmentResponse, userResponse] = await Promise.all([
        departmentService.getDepartments(1, 200),
        userService.getUsers({ pageNumber: 1, pageSize: 200 }),
      ]);
      setDepartments(departmentResponse?.data ?? []);
      setUsers(userResponse.data.data ?? []);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('employee.failedToLoadLookups'));
    }
  }, [t]);

  const loadEmployee = useCallback(async () => {
    if (!id) return;
    setLoadingData(true);
    setError(null);
    try {
      const employee = await employeeService.getById(Number(id));
      if (!employee) {
        setError(t('employee.notFound'));
        return;
      }
      setFormData({
        employeeCode: employee.employeeCode,
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email ?? '',
        phone: employee.phone ?? '',
        jobTitle: employee.jobTitle ?? '',
        departmentId: employee.departmentId ?? '',
        userId: employee.userId ?? '',
        hireDate: toDateInput(employee.hireDate),
        terminationDate: toDateInput(employee.terminationDate),
        isActive: employee.isActive,
      });
    } catch (err) {
      setError(getServerErrorMessage(err) || t('employee.failedToLoad'));
    } finally {
      setLoadingData(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadEmployee();
  }, [loadEmployee]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';

    let finalValue: any;
    if (isCheckbox) {
      finalValue = (e.target as HTMLInputElement).checked;
    } else if (name === 'departmentId' || name === 'userId') {
      finalValue = value === '' ? '' : Number(value);
    } else {
      finalValue = value;
      if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
        finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    if (!isCheckbox) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.employeeCode.trim()) {
      errors.employeeCode = t('employee.formValidation.codeRequired');
      isValid = false;
    } else if (formData.employeeCode.length > MAX_LENGTHS.employeeCode) {
      errors.employeeCode = t('employee.formValidation.codeMaxLength', { max: MAX_LENGTHS.employeeCode });
      isValid = false;
    }

    if (!formData.firstName.trim()) {
      errors.firstName = t('employee.formValidation.firstNameRequired');
      isValid = false;
    } else if (formData.firstName.length > MAX_LENGTHS.firstName) {
      errors.firstName = t('employee.formValidation.firstNameMaxLength', { max: MAX_LENGTHS.firstName });
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      errors.lastName = t('employee.formValidation.lastNameRequired');
      isValid = false;
    } else if (formData.lastName.length > MAX_LENGTHS.lastName) {
      errors.lastName = t('employee.formValidation.lastNameMaxLength', { max: MAX_LENGTHS.lastName });
      isValid = false;
    }

    if (formData.email) {
      if (formData.email.length > MAX_LENGTHS.email) {
        errors.email = t('employee.formValidation.emailMaxLength', { max: MAX_LENGTHS.email });
        isValid = false;
      } else if (!EMAIL_PATTERN.test(formData.email)) {
        errors.email = t('employee.formValidation.emailInvalid');
        isValid = false;
      }
    }

    if (formData.phone && formData.phone.length > MAX_LENGTHS.phone) {
      errors.phone = t('employee.formValidation.phoneMaxLength', { max: MAX_LENGTHS.phone });
      isValid = false;
    }

    if (formData.jobTitle && formData.jobTitle.length > MAX_LENGTHS.jobTitle) {
      errors.jobTitle = t('employee.formValidation.jobTitleMaxLength', { max: MAX_LENGTHS.jobTitle });
      isValid = false;
    }

    if (!formData.hireDate) {
      errors.hireDate = t('employee.formValidation.hireDateRequired');
      isValid = false;
    }

    if (formData.terminationDate && formData.hireDate && formData.terminationDate < formData.hireDate) {
      errors.terminationDate = t('employee.formValidation.terminationBeforeHire');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const mapFormToDTO = (): UpdateEmployeeDTO => ({
    employeeCode: formData.employeeCode.trim(),
    firstName: formData.firstName.trim(),
    lastName: formData.lastName.trim(),
    email: formData.email || undefined,
    phone: formData.phone || undefined,
    jobTitle: formData.jobTitle || undefined,
    departmentId: formData.departmentId === '' ? null : Number(formData.departmentId),
    userId: formData.userId === '' ? null : Number(formData.userId),
    // Date-only inputs; send them as UTC instants so the stored dates are the days picked.
    hireDate: new Date(`${formData.hireDate}T00:00:00Z`).toISOString(),
    terminationDate: formData.terminationDate
      ? new Date(`${formData.terminationDate}T00:00:00Z`).toISOString()
      : null,
    isActive: formData.isActive,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setSaving(true);
    try {
      await employeeService.update(Number(id), mapFormToDTO());
      navigate('/hr/employees', { state: { message: t('employee.updateSuccess') } });
    } catch (err) {
      setError(getServerErrorMessage(err) || t('employee.failedToUpdate'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="edit-container hr-form-page">
          <div className="ims-loading">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container hr-form-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/hr/employees')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('employee.editEmployee')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/hr/employees')}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving
                ? <><span className="ims-btn-spinner" />{t('common.saving')}</>
                : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('employee.editEmployee')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>
                  <div className="hr-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('employee.employeeCode')} <span className="required">*</span>
                        <span className="char-counter">{formData.employeeCode.length}/{MAX_LENGTHS.employeeCode}</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${fieldErrors.employeeCode ? 'is-invalid' : ''}`}
                        name="employeeCode"
                        value={formData.employeeCode}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.employeeCode}
                        placeholder="EMP001"
                      />
                      {fieldErrors.employeeCode && <small className="form-error">{fieldErrors.employeeCode}</small>}
                      {!fieldErrors.employeeCode && <small className="form-helper">{t('employee.employeeCodeHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('employee.hireDate')} <span className="required">*</span></label>
                      <input
                        type="date"
                        className={`form-input ${fieldErrors.hireDate ? 'is-invalid' : ''}`}
                        name="hireDate"
                        value={formData.hireDate}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.hireDate && <small className="form-error">{fieldErrors.hireDate}</small>}
                      {!fieldErrors.hireDate && <small className="form-helper">{t('employee.hireDateHint')}</small>}
                    </div>
                  </div>

                  <div className="hr-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('employee.firstName')} <span className="required">*</span>
                        <span className="char-counter">{formData.firstName.length}/{MAX_LENGTHS.firstName}</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${fieldErrors.firstName ? 'is-invalid' : ''}`}
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.firstName}
                        placeholder={t('employee.firstName')}
                      />
                      {fieldErrors.firstName && <small className="form-error">{fieldErrors.firstName}</small>}
                      {!fieldErrors.firstName && <small className="form-helper">{t('employee.firstNameHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('employee.lastName')} <span className="required">*</span>
                        <span className="char-counter">{formData.lastName.length}/{MAX_LENGTHS.lastName}</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${fieldErrors.lastName ? 'is-invalid' : ''}`}
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.lastName}
                        placeholder={t('employee.lastName')}
                      />
                      {fieldErrors.lastName && <small className="form-error">{fieldErrors.lastName}</small>}
                      {!fieldErrors.lastName && <small className="form-helper">{t('employee.lastNameHint')}</small>}
                    </div>
                  </div>

                  <div className="hr-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('employee.email')}
                        <span className="char-counter">{formData.email.length}/{MAX_LENGTHS.email}</span>
                      </label>
                      <input
                        type="email"
                        className={`form-input ${fieldErrors.email ? 'is-invalid' : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.email}
                        placeholder="name@example.com"
                      />
                      {fieldErrors.email && <small className="form-error">{fieldErrors.email}</small>}
                      {!fieldErrors.email && <small className="form-helper">{t('employee.emailHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('employee.phone')}
                        <span className="char-counter">{formData.phone.length}/{MAX_LENGTHS.phone}</span>
                      </label>
                      <input
                        type="tel"
                        className={`form-input ${fieldErrors.phone ? 'is-invalid' : ''}`}
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.phone}
                        placeholder={t('employee.phone')}
                      />
                      {fieldErrors.phone && <small className="form-error">{fieldErrors.phone}</small>}
                      {!fieldErrors.phone && <small className="form-helper">{t('employee.phoneHint')}</small>}
                    </div>
                  </div>

                  <div className="hr-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('employee.jobTitle')}
                        <span className="char-counter">{formData.jobTitle.length}/{MAX_LENGTHS.jobTitle}</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${fieldErrors.jobTitle ? 'is-invalid' : ''}`}
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.jobTitle}
                        placeholder={t('employee.jobTitlePlaceholder')}
                      />
                      {fieldErrors.jobTitle && <small className="form-error">{fieldErrors.jobTitle}</small>}
                      {!fieldErrors.jobTitle && <small className="form-helper">{t('employee.jobTitleHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('employee.department')}</label>
                      <select
                        className="form-select"
                        name="departmentId"
                        value={formData.departmentId}
                        onChange={handleInputChange}
                      >
                        <option value="">{t('employee.noDepartment')}</option>
                        {departments.map((department) => (
                          <option key={department.departmentId} value={department.departmentId}>
                            {department.departmentName}
                          </option>
                        ))}
                      </select>
                      <small className="form-helper">{t('employee.departmentHint')}</small>
                    </div>
                  </div>

                  <div className="hr-form-row">
                    <div className="form-group">
                      <label className="form-label">{t('employee.linkedUser')}</label>
                      <select
                        className="form-select"
                        name="userId"
                        value={formData.userId}
                        onChange={handleInputChange}
                      >
                        <option value="">{t('employee.noLinkedUser')}</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.firstName} {user.lastName} ({user.email})
                          </option>
                        ))}
                      </select>
                      <small className="form-helper">{t('employee.linkedUserHint')}</small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('employee.terminationDate')}</label>
                      <input
                        type="date"
                        className={`form-input ${fieldErrors.terminationDate ? 'is-invalid' : ''}`}
                        name="terminationDate"
                        value={formData.terminationDate}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.terminationDate && <small className="form-error">{fieldErrors.terminationDate}</small>}
                      {!fieldErrors.terminationDate && <small className="form-helper">{t('employee.terminationDateHint')}</small>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span>{t('employee.isActive')}</span>
                    </label>
                    <small className="form-helper">{t('employee.isActiveHint')}</small>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('employee.employeeCode')}</strong>
                  <p>{t('employee.help.employeeCode')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('employee.linkedUser')}</strong>
                  <p>{t('employee.help.linkedUser')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('employee.terminationDate')}</strong>
                  <p>{t('employee.help.terminationDate')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('employee.isActive')}</strong>
                  <p>{t('employee.help.isActive')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmployeeEditPage;
